import b4a from 'b4a'
import { domainHash, sign } from '@pine/crypto'

/**
 * Asking the server to wake somebody's phone.
 *
 * See server/relay/lib/push.js for the whole story. The short version: an
 * Android app that is not running cannot receive anything peer to peer, and
 * the one thing allowed to start it again is a Google push. This is the
 * client for that — registering where this device can be woken, and asking
 * for others to be woken when a call or a message is waiting for them.
 *
 * A wake carries nothing but "call" or "something new". Everything the woken
 * app then shows, it fetched itself, end to end encrypted, from its peers.
 */

/** Byte-identical to pushRegisterSignable() in the relay. */
export function pushRegisterSignable ({ account, at, token, action }) {
  return domainHash('pine/push/register/v1', String(action || 'register'), account, String(at), String(token || ''))
}

/** Byte-identical to pushWakeSignable() in the relay. */
export function pushWakeSignable ({ account, at, to, kind }) {
  return domainHash('pine/push/wake/v1', account, String(at), String(kind || ''), (to || []).join(','))
}

export class PushClient {
  constructor ({ url, identity }) {
    this.url = (url || '').replace(/\/+$/, '')
    this.identity = identity
  }

  get enabled () {
    return !!this.url
  }

  async _post (path, body, timeout = 8000) {
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), timeout)
    try {
      const response = await fetch(this.url + path, {
        method: 'POST',
        // Safelisted, so the Android build (cross-origin) sends no preflight.
        headers: { 'content-type': 'text/plain;charset=UTF-8' },
        body: JSON.stringify(body),
        signal: controller.signal
      })
      const payload = await response.json().catch(() => ({}))
      return { status: response.status, body: payload }
    } finally {
      clearTimeout(timer)
    }
  }

  /**
   * @param ring  this phone rings on the push itself, so the server may put
   *              who is calling into it (see server/relay/lib/push.js)
   */
  async register (token, { platform = 'android', action = 'register', ring = false } = {}) {
    if (!this.enabled || !token) return null
    const at = Date.now()
    const account = this.identity.id
    const signature = sign(pushRegisterSignable({ account, at, token, action }), this.identity.secretKey)
    try {
      const result = await this._post('/api/push/' + action, {
        account, at, token, platform, ring: !!ring, signature: b4a.toString(signature, 'hex')
      })
      return result.status === 200 ? result.body : null
    } catch {
      return null
    }
  }

  unregister (token) {
    return this.register(token, { action: 'unregister' })
  }

  /** Fire and forget: a wake that does not arrive costs a notification, not a message. */
  async wake (accounts, kind = 'msg', { video = false, answered = false } = {}) {
    const to = [...new Set((accounts || []).filter(Boolean))].slice(0, 50)
    if (!this.enabled || !to.length) return false
    const at = Date.now()
    const account = this.identity.id
    const signature = sign(pushWakeSignable({ account, at, to, kind }), this.identity.secretKey)
    try {
      const result = await this._post('/api/push/wake', {
        account, at, to, kind, video: !!video, answered: !!answered, signature: b4a.toString(signature, 'hex')
      }, 6000)
      return result.status === 200
    } catch {
      return false
    }
  }
}
